import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { KsefMonitoringApiService } from 'shared/services/api/ksef-monitoring.api.service';
import { KsefErrorDetailsDialogComponent } from 'shared/dialogs/ksef-error-details-dialog/ksef-error-details-dialog.component';
import { KsefXmlViewerDialogComponent } from 'shared/dialogs/ksef-xml-viewer-dialog/ksef-xml-viewer-dialog.component';

interface ErrorLogEntry {
  _id: string;
  documentId: string;
  documentNumber: string;
  logType: string;  // 🔑 Typ logu (auth_failed, invoice_failed, etc.)
  userId: string;
  userEmail?: string;
  errorCode: string;
  errorMessage: string;
  timestamp: Date;
  error: any;
  logData: any;  // 🔑 Pełna data z logu (do JSON preview)
  xml?: string;  // 🔑 XML faktury
  ksefNumber?: string;
}

@Component({
  selector: 'app-errors-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule
  ],
  templateUrl: './errors-list.component.html',
  styleUrls: ['./errors-list.component.scss']
})
export class ErrorsListComponent implements OnInit, OnDestroy, AfterViewInit {
  
  displayedColumns: string[] = ['timestamp', 'logType', 'documentNumber', 'userEmail', 'errorCode', 'errorMessage', 'actions'];
  dataSource = new MatTableDataSource<ErrorLogEntry>([]);
  
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  
  isLoading = true;
  totalErrors = 0;

  // Paginacja server-side
  pageIndex = 0;
  pageSize = 50;

  // Filtry
  searchQuery = '';
  selectedLogTypes: string[] = ['all'];  // Domyślnie "Wszystkie typy"
  
  // Subject do debounce wyszukiwania
  private searchSubject = new Subject<string>();
  
  // Dostępne typy logów (error types)
  errorLogTypes = [
    'invoice_failed', 
    'auth_failed', 
    'auth_refresh_failed', 
    'connection_deactivated', 
    'system_error',
    'session_failed',
    'validation_error'
  ];
  
  // Dostępne typy logów
  availableLogTypes = [
    { value: 'invoice_failed', label: 'Błąd faktury' },
    { value: 'auth_failed', label: '🔐 Błąd autentykacji' },
    { value: 'auth_refresh_failed', label: '🔄 Błąd odświeżania tokena' },
    { value: 'connection_deactivated', label: '🚫 Połączenie dezaktywowane' },
    { value: 'system_error', label: '⚠️ Błąd systemowy' },
    { value: 'session_failed', label: 'Błąd sesji' },
    { value: 'validation_error', label: 'Błąd walidacji' }
  ];
  
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _ksefApiService: KsefMonitoringApiService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Setup debounce dla wyszukiwania (300ms)
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this._unsubscribeAll)
    ).subscribe(() => {
      this.pageIndex = 0;  // Reset do pierwszej strony
      this.loadErrors();
    });

    this.loadErrors();
  }

  ngAfterViewInit(): void {
    // Paginator jest obsługiwany przez (page) event w HTML
  }

  /**
   * Handler zmiany strony w paginatorze
   */
  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadErrors();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Załaduj błędy z API (server-side pagination)
   */
  loadErrors() {
    this.isLoading = true;
    
    // Przygotuj parametry zapytania
    const params: any = {
      page: this.pageIndex + 1,  // API używa 1-based
      pageSize: this.pageSize
    };
    
    // Dodaj typy logów TYLKO gdy są wybrane konkretne filtry (nie "all")
    // Gdy "all" - wyklucz CRON/SYNC (te są w tabie Synchronizacja)
    const hasSpecificFilters = this.selectedLogTypes.length > 0 && !this.selectedLogTypes.includes('all');
    if (hasSpecificFilters) {
      params.logTypes = this.selectedLogTypes;
    } else {
      // Pokaż wszystkie OPRÓCZ CRON i SYNC (przenieione do taba Synchronizacja)
      params.logTypes = this.errorLogTypes;
    }
    
    // Dodaj wyszukiwanie jeśli podano
    if (this.searchQuery.trim()) {
      params.search = this.searchQuery.trim();
    }
    
    this._ksefApiService.getBatchLogs(params)
    .pipe(takeUntil(this._unsubscribeAll))
    .subscribe({
      next: (response) => {
        const logs = response.data.pageResults || [];
        
        // Mapuj logi na strukturę ErrorLogEntry
        const errors: ErrorLogEntry[] = logs.map((log: any) => ({
          _id: log._id,
          documentId: log.data?.documentId,
          documentNumber: log.data?.documentNumber || this.getLogTypeLabel(log.logType),
          logType: log.logType,
          userId: log.userId?._id || log.userId,
          userEmail: log.userId?.emailAddress || 'Brak email',
          errorCode: this.extractErrorCode(log.data?.error),
          errorMessage: this.extractErrorMessage(log.data?.error, log.logType),
          timestamp: new Date(log.timestamp),
          error: log.data?.error,
          logData: log.data,
          xml: log.data?.xml,
          ksefNumber: log.data?.ksefNumber
        }));
        
        this.dataSource.data = errors;
        this.totalErrors = response.data.total || 0;
        
        // Sync paginacji z odpowiedzią serwera
        this.pageIndex = (response.data.currentPage || 1) - 1;  // Convert to 0-based
        this.pageSize = response.data.pageSize || 50;
        
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Błąd podczas ładowania błędów:', error);
        this.isLoading = false;
      }
    });
  }

  /**
   * Wyszukaj po emailu użytkownika
   */
  searchByEmail(email: string) {
    if (email && email !== 'Brak email') {
      this.searchQuery = email;
      this.pageIndex = 0;
      this.loadErrors();
    }
  }

  /**
   * Wydobądź kod błędu z różnych struktur
   */
  private extractErrorCode(error: any): string {
    if (!error) return 'N/A';
    if (error.code) return error.code.toString();
    if (error.errorCode) return error.errorCode;
    if (error.status?.code) return error.status.code.toString();
    return 'UNKNOWN';
  }

  /**
   * Wydobądź komunikat błędu
   */
  private extractErrorMessage(error: any, logType?: string): string {
    // Dla logów bez szczegółów błędu (np. connection_deactivated) - nie pokazuj
    if (!error) {
      // Specjalne typy logów które nie mają szczegółów błędu
      const noErrorDetailsTypes = ['connection_deactivated', 'auth_refresh_failed'];
      if (logType && noErrorDetailsTypes.includes(logType)) {
        return '';  // Pusty string zamiast "Szczegóły niedostępne"
      }
      return 'Szczegóły niedostępne';
    }
    if (error.description) return error.description;
    if (error.message) return error.message;
    if (error.errorMessage) return error.errorMessage;
    if (error.status?.description) return error.status.description;
    return 'Nieznany błąd';
  }

  /**
   * Otwórz szczegóły błędu
   */
  openErrorDetails(error: ErrorLogEntry) {
    this.dialog.open(KsefErrorDetailsDialogComponent, {
      width: '900px',  // Szerszy dialog dla XML
      maxWidth: '95vw',
      height: '90vh',
      panelClass: 'dialog-responsive',
      data: {
        documentNumber: error.documentNumber,
        documentId: error.documentId,
        error: error.error,
        logData: error.logData,  // 🔑 Pełna data z logu (do JSON preview)
        xml: error.xml,  // 🔑 XML faktury
        timestamp: error.timestamp,
        userId: error.userId,
        userEmail: error.userEmail
      }
    });
  }

  /**
   * Otwórz XML viewer
   */
  openXmlViewer(error: ErrorLogEntry) {
    this.dialog.open(KsefXmlViewerDialogComponent, {
      width: '1000px',
      maxWidth: '95vw',
      height: '90vh',
      panelClass: 'dialog-responsive',
      data: {
        documentId: error.documentId
      }
    });
  }

  /**
   * Odśwież listę
   */
  refresh() {
    this.loadErrors();
  }

  /**
   * Pobierz kolor dla kodu błędu
   */
  getErrorCodeColor(code: string): string {
    if (code.startsWith('4')) return 'warn';
    if (code.startsWith('5')) return 'accent';
    return '';
  }

  /**
   * Handler zmiany search query (debounced)
   */
  onSearchChange() {
    this.searchSubject.next(this.searchQuery);
  }

  /**
   * Handler zmiany wyboru typów logów
   */
  onLogTypesChange(selected: string[]) {
    // Jeśli kliknięto "Wszystkie typy" - odznacz wszystkie inne
    if (selected.includes('all') && !this.selectedLogTypes.includes('all')) {
      // Kliknięto "all" - odznacz wszystkie inne, zostaw tylko "all"
      this.selectedLogTypes = ['all'];
    } else if (selected.includes('all') && selected.length > 1) {
      // Było "all" zaznaczone, kliknięto coś innego - odznacz "all"
      this.selectedLogTypes = selected.filter(t => t !== 'all');
    } else if (!selected.includes('all') && selected.length === 0) {
      // Odznaczono wszystko - zaznacz "all"
      this.selectedLogTypes = ['all'];
    } else {
      this.selectedLogTypes = selected;
    }
    
    this.pageIndex = 0;  // Reset do pierwszej strony
    this.loadErrors();
  }

  /**
   * Wybierz wszystkie typy
   */
  selectAllLogTypes() {
    this.selectedLogTypes = ['all'];
    this.pageIndex = 0;
    this.loadErrors();
  }

  /**
   * Pobierz czytelną etykietę dla typu logu (publiczna)
   */
  getLogTypeLabelPublic(logType: string): string {
    return this.getLogTypeLabel(logType);
  }

  /**
   * Pobierz czytelną etykietę dla typu logu
   */
  private getLogTypeLabel(logType: string): string {
    const labels: Record<string, string> = {
      'invoice_failed': 'Błąd faktury',
      'auth_failed': '🔐 Błąd autentykacji',
      'auth_refresh_failed': '🔄 Błąd odświeżania tokena',
      'connection_deactivated': '🚫 Połączenie dezaktywowane',
      'system_error': '⚠️ Błąd systemowy',
      'session_failed': 'Błąd sesji',
      'validation_error': 'Błąd walidacji'
    };
    return labels[logType] || logType;
  }
}

